import { get } from "@/services/axiosUitils";
import type {
  PublicLead,
  PublicLeadsResponse,
} from "@/utils/types/PublicLeads";

const base = "/tenant-core/public-leads/";

/** Unwrap common API envelopes so list + export always see `results`. */
function normalizePublicLeadsResponse(raw: unknown): PublicLeadsResponse {
  const empty: PublicLeadsResponse = {
    links: { next: null, previous: null },
    count: 0,
    page: 1,
    page_size: 10,
    results: [],
  };
  if (!raw || typeof raw !== "object") {
    return empty;
  }
  const r = raw as Record<string, unknown>;

  if (Array.isArray(r.results)) {
    const results = r.results as PublicLead[];
    return {
      links: (r.links as PublicLeadsResponse["links"]) ?? empty.links,
      count: Number(r.count) || results.length,
      page: Number(r.page) || 1,
      page_size: Number(r.page_size) || results.length || 10,
      results,
    };
  }

  const data = r.data as Record<string, unknown> | undefined;
  if (data && Array.isArray(data.results)) {
    const results = data.results as PublicLead[];
    return {
      links:
        (data.links as PublicLeadsResponse["links"]) ??
        (r.links as PublicLeadsResponse["links"]) ??
        empty.links,
      count: Number(data.count ?? r.count) || results.length,
      page: Number(data.page ?? r.page) || 1,
      page_size: Number(data.page_size ?? r.page_size) || results.length || 10,
      results,
    };
  }

  if (Array.isArray(r.data)) {
    const results = r.data as PublicLead[];
    return {
      links: (r.links as PublicLeadsResponse["links"]) ?? empty.links,
      count: Number(r.count) || results.length,
      page: Number(r.page) || 1,
      page_size: Number(r.page_size) || results.length || 10,
      results,
    };
  }

  return empty;
}

class PublicLeadsService {
  getPublicLeads = async (
    page: number = 1,
    pageSize: number = 10,
    search?: string
  ): Promise<PublicLeadsResponse> => {
    const params: Record<string, string | number> = {
      page,
      page_size: pageSize,
    };
    const q = search?.trim();
    if (q) {
      params.search = q;
    }
    const raw = await get<unknown>(base, { params });
    return normalizePublicLeadsResponse(raw);
  };
}

export default new PublicLeadsService();
